/**
 * In-memory GET cache for identical profile/feed requests in the same session.
 * Keyed by path; invalidated on force or when the handle (path) changes.
 */

type Ready = { kind: "ready"; at: number; data: unknown };
type Pending = { kind: "pending"; promise: Promise<unknown> };
type Entry = Ready | Pending;

const store = new Map<string, Entry>();

/** Match backend Redis default (~5 min). */
export const CLIENT_CACHE_TTL_MS = 5 * 60 * 1000;

export function invalidateClientCache(prefix?: string) {
  if (!prefix) {
    store.clear();
    return;
  }
  for (const key of store.keys()) {
    if (key.startsWith(prefix)) store.delete(key);
  }
}

export async function cachedGet<T>(
  key: string,
  fetcher: () => Promise<T>,
  opts?: { force?: boolean; ttlMs?: number },
): Promise<T> {
  const ttl = opts?.ttlMs ?? CLIENT_CACHE_TTL_MS;

  if (!opts?.force) {
    const hit = store.get(key);
    if (hit?.kind === "ready" && Date.now() - hit.at < ttl) {
      return hit.data as T;
    }
    if (hit?.kind === "pending") {
      return hit.promise as Promise<T>;
    }
  }

  const promise = fetcher()
    .then((data) => {
      store.set(key, { kind: "ready", at: Date.now(), data });
      return data;
    })
    .catch((err) => {
      store.delete(key);
      throw err;
    });

  store.set(key, { kind: "pending", promise });
  return promise;
}
